<template>
  <div class="lead-page">
    <!-- 筛选栏 -->
    <el-card class="filter-card" shadow="never">
      <div class="fg-row">
        <el-input
          v-model="keyword"
          class="fg-search"
          prefix-icon="el-icon-search"
          placeholder="搜索量账号 / 量名称"
          clearable
          @keyup.enter.native="reload"
          @clear="reload"
        />
        <el-button type="primary" icon="el-icon-search" @click="reload">查询</el-button>
        <el-button icon="el-icon-refresh" @click="resetFilters">重置</el-button>
        <el-button v-if="isPromo" type="primary" icon="el-icon-plus" plain class="fg-add" @click="goAdd">新增线索</el-button>
      </div>

      <div class="fg-row">
        <div class="fg-item">
          <span class="fg-label">录入时间</span>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            :clearable="true"
            style="width: 280px"
            @change="onDateRangeChange"
          />
        </div>
        <div class="fg-item">
          <span class="fg-label">成交时间</span>
          <el-date-picker
            v-model="dealRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            :clearable="true"
            style="width: 280px"
            @change="onDealRangeChange"
          />
        </div>
      </div>

      <div class="fg-row">
        <div class="fg-item">
          <span class="fg-label">渠道</span>
          <el-select v-model="filters.channel_id" placeholder="全部渠道" clearable style="width: 160px" @change="reload">
            <el-option v-for="c in channels" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </div>
        <template v-if="isCompany">
          <div class="fg-item">
            <span class="fg-label">战队</span>
            <el-select v-model="filters.unit_id" placeholder="全部战队" clearable style="width: 160px" @change="onUnitChange">
              <el-option v-for="u in units" :key="u.id" :label="u.name" :value="u.id" />
            </el-select>
          </div>
          <div class="fg-item">
            <span class="fg-label">小组</span>
            <el-select v-model="filters.team_id" placeholder="全部小组" clearable :disabled="!filters.unit_id" style="width: 160px" @change="onTeamChange">
              <el-option v-for="t in teams" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </div>
          <div class="fg-item">
            <span class="fg-label">员工</span>
            <el-select v-model="filters.staff_id" placeholder="全部员工" clearable :disabled="!filters.team_id" style="width: 160px" @change="reload">
              <el-option v-for="s in staffs" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
          </div>
        </template>
        <!-- 组长/战队长：按本职位员工筛选 -->
        <div v-if="isTeam || isUnit" class="fg-item">
          <span class="fg-label">员工</span>
          <el-select v-model="filters.staff_id" placeholder="全部员工" clearable style="width: 160px" @change="reload">
            <el-option v-for="s in staffs" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </div>
      </div>
    </el-card>

    <!-- 状态筛选 -->
    <div class="status-row">
      <el-radio-group v-model="status" size="small" @change="reload">
        <el-radio-button v-for="tab in statusTabs" :key="String(tab.value)" :label="tab.value">{{ tab.label }}</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 统计条 -->
    <el-card class="stats-bar" shadow="never">
      <div class="stat-cell">
        <div class="stat-num-row">
          <span class="stat-num">{{ stats.receive }}</span>
          <el-tooltip content="选定条件下，接收的线索总数量" placement="top">
            <i class="el-icon-question stat-help"></i>
          </el-tooltip>
        </div>
        <span class="stat-cap">接收量</span>
      </div>
      <div class="stat-cell">
        <div class="stat-num-row">
          <span class="stat-num">{{ stats.added }}</span>
          <span class="stat-pct">{{ stats.add_rate }}%</span>
          <el-tooltip content="选定条件下，已经添加的总数量（添加率=添加量÷接收量）" placement="top">
            <i class="el-icon-question stat-help"></i>
          </el-tooltip>
        </div>
        <span class="stat-cap">添加量</span>
      </div>
      <div class="stat-cell">
        <div class="stat-num-row">
          <span class="stat-num">{{ stats.deal }}</span>
          <span class="stat-pct">{{ stats.deal_rate }}%</span>
          <el-tooltip content="选定条件下，已经成交的总数量（成交率=成交量÷接收量）" placement="top">
            <i class="el-icon-question stat-help"></i>
          </el-tooltip>
        </div>
        <span class="stat-cap">成交量</span>
      </div>
      <div class="stat-cell">
        <div class="stat-num-row">
          <span class="stat-num">{{ stats.convert_rate }}%</span>
          <el-tooltip content="选定条件下，成交量÷添加量" placement="top">
            <i class="el-icon-question stat-help"></i>
          </el-tooltip>
        </div>
        <span class="stat-cap">转化率</span>
      </div>
    </el-card>

    <!-- 列表 -->
    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="list"
        :header-cell-style="{ background: '#F7F9FC', color: '#18233C', fontWeight: 600 }"
        :row-style="{ cursor: 'pointer' }"
        empty-text="暂无线索数据"
        @row-click="goDetail"
      >
        <el-table-column prop="liang_name" label="量名称" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <div class="cell-stack">
              <div class="cell-line">量账号：<span class="cell-line-v">{{ row.liang_account || '—' }}</span></div>
              <div class="cell-line">量名称：<span class="cell-line-v">{{ row.liang_name || '—' }}</span></div>
              <div class="cell-line">目的：<span class="cell-line-v">{{ row.purpose_text }}</span></div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="channel_name" label="来源渠道" min-width="100">
          <template slot-scope="{ row }">
            <span>{{ row.channel_name || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sales_name" label="销售" min-width="90">
          <template slot-scope="{ row }">
            <span>{{ row.sales_name || '未分配' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="from_name" label="推广" min-width="90">
          <template slot-scope="{ row }">
            <span>{{ row.from_name || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="已加微信" min-width="90" align="center">
          <template slot-scope="{ row }">
            <el-switch
              :value="row.is_added === 1"
              :disabled="!canToggleLead(row)"
              active-color="#3668FC"
              @click.native.stop
              @change="val => onToggleAdded(row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="已成交" min-width="90" align="center">
          <template slot-scope="{ row }">
            <el-switch
              :value="parseInt(row.status) === 3"
              :disabled="!canToggleDeal(row)"
              active-color="#16A34A"
              @click.native.stop
              @change="val => onToggleDeal(row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="录入时间" min-width="150">
          <template slot-scope="{ row }">
            <span class="cell-time">{{ formatDateTime(row.createtime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="成交时间" min-width="150">
          <template slot-scope="{ row }">
            <span class="cell-time">{{ row.dealtime ? formatDateTime(row.dealtime) : '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click.stop="goDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页器 -->
      <div class="pager-row">
        <div class="pager-info-left" v-if="total > 0">
          <span class="info-tag">
            显示第 <span class="num">{{ (page - 1) * limit + 1 }}</span> -
            <span class="num">{{ Math.min(page * limit, total) }}</span> 条，
            共 <span class="num">{{ total }}</span> 条记录
          </span>
        </div>
        <el-pagination
          background
          :layout="pagerLayout"
          :pager-count="7"
          :total="total"
          :current-page.sync="page"
          :page-size.sync="limit"
          :page-sizes="[10, 20, 50, 100]"
          @size-change="onSizeChange"
          @current-change="loadData"
        />
      </div>
    </el-card>

    <!-- 新增线索弹窗（仅推广人员可见入口） -->
    <el-dialog
      :title="addStep === 'form' ? '录入线索' : '录入成功'"
      :visible.sync="addDialogVisible"
      width="520px"
      :close-on-click-modal="addStep !== 'form'"
      @closed="onAddDialogClosed"
    >
      <!-- 步骤一：表单 -->
      <el-form v-if="addStep === 'form'" ref="addForm" :model="addForm" :rules="addRules" label-position="top" @submit.native.prevent>
        <el-form-item label="来源渠道" prop="channel_id">
          <el-radio-group v-model="addForm.channel_id">
            <el-radio v-for="c in channels" :key="c.id" :label="c.id" border>{{ c.name }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="量账号" prop="liang_account">
          <el-input v-model="addForm.liang_account" placeholder="抖音号 / 微信号 / QQ号" :maxlength="100" clearable />
        </el-form-item>
        <el-form-item label="量名称">
          <el-input v-model="addForm.liang_name" placeholder="选填，最多 50 字" :maxlength="50" clearable />
        </el-form-item>
        <el-form-item label="目的">
          <el-radio-group v-model="addForm.purpose">
            <el-radio :label="0">未知</el-radio>
            <el-radio :label="1">学习</el-radio>
            <el-radio :label="2">兼职</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <!-- 步骤二：成功 + 销售二维码 -->
      <div v-else class="add-result">
        <div class="add-result-head">
          <div class="add-result-icon">
            <i class="el-icon-success"></i>
          </div>
          <div class="add-result-title">录入成功</div>
          <div class="add-result-sub">线索已{{ addResult.assign_type === 1 ? '自动轮询' : '手动指定' }}分配销售</div>
        </div>

        <div v-if="addResult.sales" class="add-result-sales">
          <div class="sales-info-row">
            <span class="sales-info-label">销售顾问</span>
            <span class="sales-info-value">{{ addResult.sales.name }}</span>
          </div>
          <div v-if="addResult.sales.wechat" class="sales-info-row">
            <span class="sales-info-label">微信号</span>
            <span class="sales-info-value">{{ addResult.sales.wechat }}</span>
          </div>
          <div class="qrcode-wrap">
            <el-image
              v-if="addResult.sales.wechat_qrcode"
              :src="qrcodeUrl"
              fit="contain"
              class="qrcode-img"
              :preview-src-list="[qrcodeUrl]"
            />
            <div v-else class="qrcode-img qrcode-empty">
              <i class="el-icon-picture-outline qrcode-empty-ico"></i>
              <span class="qrcode-empty-text">销售未上传二维码</span>
            </div>
          </div>
          <span v-if="addResult.sales.wechat_qrcode" class="qrcode-tip">请客户扫码添加销售微信</span>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <template v-if="addStep === 'form'">
          <el-button @click="addDialogVisible = false">取 消</el-button>
          <el-button type="primary" :loading="addLoading" @click="doAdd">提交并分配销售</el-button>
        </template>
        <template v-else>
          <el-button @click="resetAdd">继续录入</el-button>
          <el-button type="primary" @click="addDialogVisible = false">完 成</el-button>
        </template>
      </div>
    </el-dialog>

    <!-- 线索详情弹框 -->
    <el-dialog title="线索详情" :visible.sync="detailVisible" width="520px">
      <div v-loading="detailLoading" class="detail-body">
        <template v-if="detail.id">
          <div class="detail-head">
            <div class="detail-avatar">{{ (detail.liang_name || detail.liang_account || '客').substring(0, 1) }}</div>
            <div class="detail-head-main">
              <div class="detail-name-row">
                <span class="detail-name">{{ detail.liang_name || '未填写名称' }}</span>
                <el-tag :type="statusTagType(detail.status)" size="small">{{ detail.status_text }}</el-tag>
                <el-tag v-if="detail.is_added === 1" type="success" size="small" effect="plain">已加微信</el-tag>
              </div>
              <div class="detail-account">{{ detail.liang_account }}</div>
            </div>
          </div>

          <div class="detail-grid">
            <div class="d-item">
              <span class="d-label">目的</span>
              <span class="d-value">{{ detail.purpose_text }}</span>
            </div>
            <div class="d-item">
              <span class="d-label">来源渠道</span>
              <span class="d-value">{{ detail.channel_name || '—' }}</span>
            </div>
            <div class="d-item">
              <span class="d-label">分配销售</span>
              <span class="d-value">{{ detail.sales_name || '未分配' }}</span>
            </div>
            <div class="d-item">
              <span class="d-label">分配方式</span>
              <span class="d-value">{{ detail.assign_type === 1 ? '系统轮询' : '手动指定' }}</span>
            </div>
            <div class="d-item">
              <span class="d-label">推广</span>
              <span class="d-value">{{ detail.from_name || '—' }}</span>
            </div>
            <div class="d-item">
              <span class="d-label">录入时间</span>
              <span class="d-value">{{ formatDateTime(detail.createtime) }}</span>
            </div>
            <div v-if="detail.assigntime" class="d-item">
              <span class="d-label">分配时间</span>
              <span class="d-value">{{ formatDateTime(detail.assigntime) }}</span>
            </div>
            <div v-if="detail.addtime" class="d-item">
              <span class="d-label">添加时间</span>
              <span class="d-value">{{ formatDateTime(detail.addtime) }}</span>
            </div>
            <div v-if="detail.dealtime" class="d-item">
              <span class="d-label">成交时间</span>
              <span class="d-value">{{ formatDateTime(detail.dealtime) }}</span>
            </div>
          </div>

          <!-- 管理员操作：切换已加微信 / 已成交 / 转移 / 删除 -->
          <div v-if="isCompany" class="detail-ops">
            <div class="detail-ops-title">管理员操作</div>
            <div class="detail-ops-row">
              <span class="ops-label">已加微信</span>
              <el-switch :value="detail.is_added === 1" active-color="#3668FC" @change="val => onToggleAdded(detail, val, true)" />
            </div>
            <div class="detail-ops-row">
              <span class="ops-label">已成交</span>
              <el-switch :value="parseInt(detail.status) === 3" active-color="#16A34A" @change="val => onToggleDeal(detail, val, true)" />
            </div>
            <div class="detail-ops-row">
              <span class="ops-label">转移销售</span>
              <el-select v-model="transferStaffId" placeholder="选择销售员工" size="small" style="width: 200px">
                <el-option v-for="s in salesStaffs" :key="s.id" :label="s.name + (s.id === detail.assign_staff_id ? '（当前）' : '')" :value="s.id" :disabled="s.id === detail.assign_staff_id" />
              </el-select>
              <el-button type="primary" size="small" :disabled="!transferStaffId" :loading="transferLoading" @click="doTransfer">转移</el-button>
            </div>
            <div class="detail-ops-row">
              <span class="ops-label">删除线索</span>
              <el-button type="danger" size="small" plain @click="doDelete">删除</el-button>
            </div>
          </div>

          <!-- 战队长/小组长：删除本范围线索 -->
          <div v-if="isGroupManager" class="detail-ops">
            <div class="detail-ops-title">战队长/组长操作</div>
            <div class="detail-ops-row">
              <span class="ops-label">已加微信</span>
              <el-switch :value="detail.is_added === 1" active-color="#3668FC" @change="val => onToggleAdded(detail, val, true)" />
            </div>
            <div class="detail-ops-row">
              <span class="ops-label">删除线索</span>
              <el-button type="danger" size="small" plain @click="doDelete">删除</el-button>
            </div>
          </div>

          <!-- 销售本人：标记已添加 -->
          <div v-if="canMarkAdded" class="detail-ops">
            <div class="detail-ops-title">销售操作</div>
            <el-button type="primary" size="small" @click="doMarkAdded">标记已添加（客户已加我微信）</el-button>
          </div>
        </template>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { API_BASE } from '@/config'
import { leadList, leadDetail, leadAdd, leadMarkAdded, leadToggleAdded, leadToggleDeal, leadTransfer, leadDelete } from '@/api/lead'
import { channelList } from '@/api/base'
import { unitList, teamList, staffList } from '@/api/org'

function emptyFilters() {
  return {
    channel_id: null,
    unit_id: null,
    team_id: null,
    staff_id: null
  }
}

function emptyAddForm() {
  return {
    channel_id: null,
    liang_account: '',
    liang_name: '',
    purpose: 0
  }
}

export default {
  name: 'LeadList',
  data() {
    return {
      keyword: '',
      status: '',
      statusTabs: [
        { label: '全部', value: '' },
        { label: '待添加', value: 1 },
        { label: '已添加', value: 2 },
        { label: '待成交', value: 4 },
        { label: '已成交', value: 3 }
      ],
      list: [],
      page: 1,
      limit: 20,
      total: 0,
      loading: false,
      // 筛选
      dateRange: null,   // [start, end] yyyy-MM-dd
      dealRange: null,
      filters: emptyFilters(),
      channels: [],
      units: [],
      teams: [],
      staffs: [],
      // 统计条
      stats: { receive: 0, added: 0, deal: 0, add_rate: 0, deal_rate: 0, convert_rate: 0 },
      // 新增线索弹窗
      addDialogVisible: false,
      addStep: 'form',   // form 表单 | result 成功结果
      addForm: emptyAddForm(),
      addRules: {
        channel_id: [{ required: true, message: '请选择来源渠道', trigger: 'change' }],
        liang_account: [
          { required: true, message: '请填写量账号', trigger: 'blur' },
          { max: 100, message: '最多 100 字', trigger: 'blur' }
        ]
      },
      addLoading: false,
      addResult: { lead_id: 0, assign_type: 1, sales: null },
      // 详情弹框
      detailVisible: false,
      detailLoading: false,
      detail: {},
      // 管理员操作
      transferStaffId: null,
      transferLoading: false,
      salesStaffs: []
    }
  },
  computed: {
    ...mapState(['user']),
    isCompany() {
      return !!(this.user && this.user.type === 'company')
    },
    /* 战队长(unit) / 小组长(team)：可删除本范围线索 */
    isGroupManager() {
      return !!(this.user && (this.user.type === 'unit' || this.user.type === 'team'))
    },
    /* 小组长（team）：可在线索中按本组员工筛选 */
    isTeam() {
      return !!(this.user && this.user.type === 'team')
    },
    /* 战队长（unit）：可在线索中按本战队员工筛选 */
    isUnit() {
      return !!(this.user && this.user.type === 'unit')
    },
    /* 推广人员：仅可录入线索，列表只读（自己录入的） */
    isPromo() {
      return !!(this.user && this.user.type === 'staff' && parseInt(this.user.is_promo) === 1)
    },
    statusTagType() {
      return s => ({ 1: 'warning', 2: '', 3: 'success' }[parseInt(s)] || 'info')
    },
    pagerLayout() {
      return 'total, sizes, prev, pager, next, jumper'
    },
    /* 销售本人（被分配的）对待添加线索可标记 */
    canMarkAdded() {
      if (!this.user || this.user.type !== 'staff') return false
      if (parseInt(this.detail.status) !== 1) return false
      return parseInt(this.detail.assign_staff_id) === parseInt(this.user.id)
    },
    /* 二维码地址：相对路径拼后端域名 */
    qrcodeUrl() {
      const p = this.addResult.sales && this.addResult.sales.wechat_qrcode
      if (!p) return ''
      if (/^https?:\/\//.test(p)) return p
      const host = API_BASE.replace(/\/api$/, '')
      return host + p
    }
  },
  created() {
    this.loadChannels()
    if (this.isCompany) {
      this.loadUnits()
      this.loadSalesStaffs()
    }
    if (this.isTeam) {
      this.loadStaffs(null, this.user.id)
    } else if (this.isUnit) {
      this.loadStaffs(this.user.id, null)
    }
    this.reload()
  },
  methods: {
    /* ===== 数据加载 ===== */
    reload() {
      this.page = 1
      this.loadData()
    },
    loadData() {
      if (this.loading) return
      this.loading = true
      const params = { page: this.page, limit: this.limit }
      if (this.keyword) params.keyword = this.keyword
      if (this.status !== '') params.status = this.status
      if (this.dateRange && this.dateRange[0]) {
        params.start_date = this.dateRange[0]
        params.end_date = this.dateRange[1] || this.dateRange[0]
      }
      if (this.dealRange && this.dealRange[0]) {
        params.deal_start_date = this.dealRange[0]
        params.deal_end_date = this.dealRange[1] || this.dealRange[0]
      }
      if (this.filters.channel_id) params.channel_id = this.filters.channel_id
      if (this.isCompany) {
        if (this.filters.unit_id) params.unit_id = this.filters.unit_id
        if (this.filters.team_id) params.team_id = this.filters.team_id
      }
      // 员工筛选：管理员按所选战队类型；组长按本组类型；战队长按本战队类型
      if (this.filters.staff_id) {
        let isSale = true
        if (this.isTeam) {
          isSale = parseInt(this.user && this.user.team_type) === 2
        } else if (this.isUnit) {
          isSale = parseInt(this.user && this.user.unit_type) === 2
        } else if (this.filters.unit_id) {
          const u = this.units.find(x => parseInt(x.id) === parseInt(this.filters.unit_id))
          isSale = u ? parseInt(u.type) === 2 : true
        }
        if (isSale) params.assign_staff_id = this.filters.staff_id
        else params.from_staff_id = this.filters.staff_id
      }
      leadList(params)
        .then(data => {
          this.list = data.list || []
          this.total = data.total || 0
          this.stats = data.stats || this.stats
        })
        .catch(() => {
          this.list = []
          this.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadChannels() {
      channelList()
        .then(data => { this.channels = data.list || [] })
        .catch(() => {})
    },
    loadUnits() {
      unitList()
        .then(data => { this.units = data.list || [] })
        .catch(() => {})
    },
    loadTeams(unitId) {
      teamList({ unit_id: unitId })
        .then(data => { this.teams = data.list || [] })
        .catch(() => {})
    },
    loadStaffs(unitId, teamId) {
      const params = {}
      if (unitId) params.unit_id = unitId
      if (teamId) params.team_id = teamId
      staffList(params)
        .then(data => { this.staffs = data.list || [] })
        .catch(() => {})
    },
    /* ===== 筛选操作 ===== */
    onDateRangeChange(val) {
      this.dateRange = val
      this.reload()
    },
    onDealRangeChange(val) {
      this.dealRange = val
      this.reload()
    },
    onUnitChange() {
      this.filters.team_id = null
      this.filters.staff_id = null
      this.teams = []
      this.staffs = []
      if (this.filters.unit_id) this.loadTeams(this.filters.unit_id)
      this.reload()
    },
    onTeamChange() {
      this.filters.staff_id = null
      this.staffs = []
      if (this.filters.team_id) this.loadStaffs(this.filters.unit_id, this.filters.team_id)
      this.reload()
    },
    resetFilters() {
      this.keyword = ''
      this.dateRange = null
      this.dealRange = null
      this.filters = emptyFilters()
      this.teams = []
      this.staffs = []
      this.reload()
    },
    /* ===== 分页 ===== */
    onSizeChange(size) {
      this.limit = size
      this.reload()
    },
    /* ===== 全部销售战队员工（管理员转移下拉用） ===== */
    loadSalesStaffs() {
      unitList()
        .then(du => {
          const saleUnitIds = (du.list || []).filter(u => parseInt(u.type) === 2).map(u => parseInt(u.id))
          return staffList({}).then(ds => ({ ds, saleUnitIds }))
        })
        .then(({ ds, saleUnitIds }) => {
          this.salesStaffs = (ds.list || []).filter(s => saleUnitIds.indexOf(parseInt(s.unit_id)) > -1 && parseInt(s.status) === 1)
        })
        .catch(() => {})
    },
    /* ===== 详情弹框 ===== */
    goDetail(row) {
      this.detail = {}
      this.transferStaffId = null
      this.detailVisible = true
      this.loadDetail(row.id)
    },
    loadDetail(id) {
      this.detailLoading = true
      leadDetail(id)
        .then(data => { this.detail = data.lead || {} })
        .catch(() => {
          this.detailVisible = false
        })
        .finally(() => { this.detailLoading = false })
    },
    /* ===== 已加微信开关（管理员） ===== */
    /* 可切换已加微信/成交：机构管理员 或 归属销售本人 */
    canToggleLead(row) {
      // 已加微信：机构管理员 或 战队长/组长（本范围）或 归属销售本人可改
      if (this.isCompany) return true
      if (!this.user) return false
      if (this.user.type === 'unit' || this.user.type === 'team') return true // 列表/详情已限定本范围
      return !!(this.user.type === 'staff' && row && parseInt(row.assign_staff_id) === parseInt(this.user.id))
    },
    /* 已成交：仍仅管理员 或 归属销售本人可改 */
    canToggleDeal(row) {
      if (this.isCompany) return true
      return !!(this.user && this.user.type === 'staff' && row && parseInt(row.assign_staff_id) === parseInt(this.user.id))
    },
    onToggleAdded(row, val, isDetail) {
      if (!this.canToggleLead(row)) return
      const next = val ? 1 : 0
      leadToggleAdded(row.id, !!next)
        .then(() => {
          if (isDetail) {
            this.detail.is_added = next
          } else {
            this.$set(row, 'is_added', next)
          }
          this.$message.success(next ? '已标记添加微信' : '已取消添加标记')
          this.loadData() // 添加量口径=is_added，刷新统计
        })
        .catch(() => {})
    },
    /* ===== 已成交开关（管理员） ===== */
    onToggleDeal(row, val, isDetail) {
      if (!this.canToggleDeal(row)) return
      leadToggleDeal(row.id, !!val)
        .then(() => {
          if (isDetail) {
            this.loadDetail(this.detail.id)
          } else {
            this.$set(row, 'status', val ? 3 : (row.is_added === 1 ? 2 : 1))
            this.$set(row, 'dealtime', val ? Math.floor(Date.now() / 1000) : 0)
          }
          this.$message.success(val ? '已标记成交' : '已取消成交')
          this.loadData() // 成交量/转化率口径变化，刷新统计
        })
        .catch(() => {})
    },
    /* ===== 转移销售（管理员） ===== */
    doTransfer() {
      if (!this.transferStaffId || this.transferLoading) return
      this.transferLoading = true
      leadTransfer(this.detail.id, this.transferStaffId)
        .then(() => {
          this.$message.success('转移成功')
          this.transferStaffId = null
          this.loadDetail(this.detail.id)
          this.loadData()
        })
        .catch(() => {})
        .finally(() => { this.transferLoading = false })
    },
    /* ===== 删除线索（管理员） ===== */
    doDelete() {
      this.$confirm('确定删除该线索吗？删除后不可恢复。', '删除确认', {
        confirmButtonText: '删 除',
        cancelButtonText: '取 消',
        type: 'warning'
      }).then(() => {
        leadDelete(this.detail.id)
          .then(() => {
            this.$message.success('已删除')
            this.detailVisible = false
            this.loadData()
          })
          .catch(() => {})
      }).catch(() => {})
    },
    /* ===== 销售本人标记已添加 ===== */
    doMarkAdded() {
      leadMarkAdded(this.detail.id)
        .then(() => {
          this.$message.success('已标记为已添加')
          this.loadDetail(this.detail.id)
          this.loadData()
        })
        .catch(() => {})
    },
    /* ===== 新增线索弹窗 ===== */
    goAdd() {
      this.addStep = 'form'
      this.addForm = emptyAddForm()
      this.addDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.addForm) this.$refs.addForm.clearValidate()
      })
    },
    doAdd() {
      if (this.addLoading) return
      this.$refs.addForm.validate(valid => {
        if (!valid) return
        this.addLoading = true
        leadAdd({
          channel_id: this.addForm.channel_id,
          liang_account: (this.addForm.liang_account || '').trim(),
          liang_name: (this.addForm.liang_name || '').trim(),
          purpose: this.addForm.purpose
        })
          .then(data => {
            this.addResult = data
            this.addStep = 'result'
            // 静默刷新列表（保持当前筛选与页码）
            this.loadData()
          })
          .catch(() => {})
          .finally(() => {
            this.addLoading = false
          })
      })
    },
    resetAdd() {
      this.addStep = 'form'
      this.addForm = emptyAddForm()
      this.$nextTick(() => {
        if (this.$refs.addForm) this.$refs.addForm.clearValidate()
      })
    },
    onAddDialogClosed() {
      // 关闭后回到表单态，下次打开是干净表单
      this.addStep = 'form'
      this.addForm = emptyAddForm()
    },
    /* ===== 格式化 ===== */
    formatDateTime(ts) {
      if (!ts) return '—'
      const d = new Date(parseInt(ts) * 1000)
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    }
  }
}
</script>

<style scoped>
.lead-page {
  padding: 0;
}

/* ===== 筛选栏 ===== */
.filter-card {
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid #E5E9F2;
}

.fg-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-bottom: 12px;
}

.fg-row:last-child { margin-bottom: 0; }

.fg-search { width: 260px; }
.fg-add { margin-left: auto; }

.fg-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fg-label { font-size: 13px; color: #8A94AD; flex-shrink: 0; }

/* ===== 状态筛选 ===== */
.status-row {
  margin-bottom: 14px;
}

/* ===== 统计条 ===== */
.stats-bar {
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid #E5E9F2;
  padding: 6px 8px;
}

/* el-card 内容包裹在 .el-card__body 内，grid 需作用于 body 才能让四个指标同一行 */
.stats-bar ::v-deep .el-card__body {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 0;
}

.stat-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  position: relative;
}

.stat-cell + .stat-cell::before {
  content: '';
  position: absolute;
  left: 0;
  top: 20%;
  bottom: 20%;
  width: 1px;
  background: #F2F4F8;
}

.stat-num-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.stat-num { font-size: 21px; font-weight: 700; color: #18233C; line-height: 1.2; }
.stat-pct { font-size: 12px; color: #3668FC; font-weight: 600; }

.stat-help {
  font-size: 14px;
  color: #B4BDCF;
  cursor: help;
  transition: color 0.15s ease;
}

.stat-help:hover { color: #3668FC; }

.stat-cap { font-size: 12px; color: #8A94AD; }

/* ===== 表格卡片 ===== */
.table-card {
  border-radius: 10px;
  border: 1px solid #E5E9F2;
}

/* 量名称单元格 */
.cell-stack { display: flex; flex-direction: column; gap: 3px; }
.cell-line { font-size: 12px; color: #8A94AD; line-height: 1.35; }
.cell-line-v { color: #2B3653; font-weight: 500; }

.cell-name {
  display: flex;
  align-items: center;
}

.cell-avatar {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #EDF3FE;
  border: 1px solid #DCE6FB;
  color: #3668FC;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cell-name-main {
  margin-left: 10px;
  min-width: 0;
}

.cell-name-text {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #18233C;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-name-sub {
  display: block;
  font-size: 11px;
  color: #8A94AD;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-time {
  font-size: 12px;
  color: #5A6580;
  font-variant-numeric: tabular-nums;
}

/* 分页器行 */
.pager-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 8px 8px;
  border-top: 1px solid #F2F4F8;
  margin-top: 4px;
}

.pager-info-left {
  font-size: 12px;
  color: #8A94AD;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: #F4F6FB;
  border-radius: 6px;
  color: #5A6580;
  font-variant-numeric: tabular-nums;
}

.info-tag .num { color: #3668FC; font-weight: 600; margin: 0 2px; }

/* ===== 新增线索弹窗：成功视图 ===== */
.add-result {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.add-result-head {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.add-result-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #E8F8EF;
  color: #16A34A;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-result-title {
  margin-top: 10px;
  font-size: 17px;
  font-weight: 700;
  color: #18233C;
}

.add-result-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #8A94AD;
}

.add-result-sales {
  margin-top: 18px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sales-info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
}

.sales-info-label { font-size: 12px; color: #8A94AD; }
.sales-info-value { font-size: 14px; color: #18233C; font-weight: 600; }

.qrcode-wrap { margin-top: 10px; }

.qrcode-img {
  width: 180px;
  height: 180px;
  border-radius: 10px;
  border: 1px solid #E5E9F2;
  background: #FAFBFD;
}

.qrcode-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.qrcode-empty-ico { font-size: 34px; color: #DCE1EC; }
.qrcode-empty-text { font-size: 12px; color: #B4BDCF; }

.qrcode-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #8A94AD;
}

/* ===== 详情弹框 ===== */
.detail-body { min-height: 100px; }

.detail-head {
  display: flex;
  align-items: center;
  padding-bottom: 14px;
  border-bottom: 1px solid #F2F4F8;
}

.detail-avatar {
  width: 44px;
  height: 44px;
  border-radius: 11px;
  background: #EDF3FE;
  border: 1px solid #DCE6FB;
  color: #3668FC;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-head-main { margin-left: 12px; min-width: 0; }
.detail-name-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.detail-name { font-size: 16px; font-weight: 700; color: #18233C; }
.detail-account { display: block; margin-top: 4px; font-size: 12px; color: #8A94AD; word-break: break-all; }

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 24px;
  padding: 14px 0;
}

.d-item { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.d-label { font-size: 11px; color: #B4BDCF; }
.d-value { font-size: 13px; color: #2B3653; word-break: break-all; font-variant-numeric: tabular-nums; }

.detail-ops {
  border-top: 1px solid #F2F4F8;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-ops-title {
  font-size: 12px;
  font-weight: 600;
  color: #8A94AD;
}

.detail-ops-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ops-label { font-size: 12px; color: #5A6580; width: 60px; flex-shrink: 0; }

/* ===== Element UI 品牌色统一 ===== */
::v-deep .el-button--primary {
  background: #3668FC;
  border-color: #3668FC;
}

::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }

::v-deep .el-button--primary.is-plain {
  color: #3668FC;
  background: #EDF3FE;
  border-color: #C8D6FA;
}

::v-deep .el-button--primary.is-plain:hover,
::v-deep .el-button--primary.is-plain:focus {
  background: #3668FC;
  border-color: #3668FC;
  color: #FFF;
}

::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #3668FC;
  border-color: #3668FC;
  box-shadow: -1px 0 0 0 #3668FC;
}

::v-deep .el-radio.is-bordered.is-checked {
  border-color: #3668FC;
}

::v-deep .el-radio.is-bordered.is-checked .el-radio__label { color: #3668FC; }
::v-deep .el-radio.is-bordered.is-checked .el-radio__inner {
  border-color: #3668FC;
  background: #3668FC;
}
::v-deep .el-radio__input.is-checked .el-radio__inner {
  border-color: #3668FC;
  background: #3668FC;
}
::v-deep .el-radio__input.is-checked + .el-radio__label {
  color: #3668FC;
}

::v-deep .el-table__row:hover > td {
  background: #F5F8FF !important;
}

/* 分页器页码激活态品牌色 */
::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #3668FC;
  border-color: #3668FC;
}

::v-deep .el-dialog {
  border-radius: 12px;
}
</style>
